import {
  AfterCallRisk,
  AgentPerformanceData,
  RiskAnalysisContent,
  RiskAnalysisData,
  RiskAxes,
  RiskAxisScore,
  RiskTypeDetail,
  RiskTypes
} from "../types/risk-analysis";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const safeNumber = (value: unknown, fallback: number): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return fallback;
};

const safeString = (value: unknown, fallback: string): string => {
  if (typeof value === "string") {
    return value;
  }
  return fallback;
};

const safeStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((item): item is string => typeof item === "string");
};

const normalizeAxisScore = (raw: Partial<RiskAxisScore> | undefined): RiskAxisScore => ({
  score: safeNumber(raw?.score, 0),
  justification: safeString(raw?.justification, ""),
  evidence: safeStringArray(raw?.evidence)
});

const normalizeRiskTypeDetail = (raw: Partial<RiskTypeDetail> | undefined): RiskTypeDetail => ({
  score: safeNumber(raw?.score, 0),
  justification: safeString(raw?.justification, ""),
  evidence: safeStringArray(raw?.evidence)
});

const normalizeAxes = (raw: Partial<RiskAxes> | undefined): RiskAxes => ({
  severity: normalizeAxisScore(raw?.severity),
  likelihood: normalizeAxisScore(raw?.likelihood),
  timeSensitivity: normalizeAxisScore(raw?.timeSensitivity)
});

const normalizeRiskTypes = (raw: Partial<RiskTypes> | undefined): RiskTypes => ({
  financialRisk: normalizeRiskTypeDetail(raw?.financialRisk),
  complianceRisk: normalizeRiskTypeDetail(raw?.complianceRisk),
  operationalRisk: normalizeRiskTypeDetail(raw?.operationalRisk),
  reputationalRisk: normalizeRiskTypeDetail(raw?.reputationalRisk)
});

const normalizeAfterCallRisk = (raw: Partial<AfterCallRisk> | undefined): AfterCallRisk => ({
  evidence: safeStringArray(raw?.evidence)
});

const normalizeAgentPerformance = (
  raw: Partial<AgentPerformanceData> | undefined
): AgentPerformanceData | undefined => {
  if (!raw) {
    return undefined;
  }
  return {
    summary: safeString(raw.summary, ""),
    afterCallRisk: normalizeAfterCallRisk(raw.afterCallRisk),
    beforeCallRisk: raw.beforeCallRisk ?? {}
  };
};

const parseIfJsonString = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const normalizeRiskAnalysisData = (
  raw: Partial<RiskAnalysisData> | string | undefined
): RiskAnalysisData | undefined => {
  const parsed = typeof raw === "string" ? parseIfJsonString(raw) : raw;
  if (!isPlainObject(parsed)) {
    return undefined;
  }
  const data = parsed as Partial<RiskAnalysisData>;
  return {
    overallIndicator: data.overallIndicator ?? "UNKNOWN",
    overallJustification: safeString(data.overallJustification, ""),
    evidence: safeStringArray(data.evidence),
    axes: normalizeAxes(data.axes),
    riskTypes: normalizeRiskTypes(data.riskTypes),
    recommendations: safeStringArray(data.recommendations)
  };
};

const EMPTY_CONTENT: RiskAnalysisContent = {
  riskAnalysis: undefined,
  agentPerformance: undefined,
  process_decision: undefined,
  reason_description: undefined
};

export const normalizeRiskAnalysisContent = (raw: unknown): RiskAnalysisContent => {
  if (!isPlainObject(raw)) {
    return EMPTY_CONTENT;
  }
  const content = raw as Partial<RiskAnalysisContent>;
  return {
    riskAnalysis: normalizeRiskAnalysisData(content.riskAnalysis),
    agentPerformance: normalizeAgentPerformance(content.agentPerformance),
    process_decision: typeof content.process_decision === "string" ? content.process_decision : undefined,
    reason_description: typeof content.reason_description === "string" ? content.reason_description : undefined
  };
};

const isScoredItemEmpty = (item: RiskAxisScore | RiskTypeDetail): boolean =>
  item.score === 0 && item.justification === "" && item.evidence.length === 0;

export const hasRiskAnalysisData = (
  data: RiskAnalysisData | undefined
): data is RiskAnalysisData => {
  if (!data) {
    return false;
  }

  const hasIndicator = data.overallIndicator !== "UNKNOWN";
  const hasJustification = data.overallJustification !== "";
  const hasEvidence = data.evidence.length > 0;
  const hasRecommendations = data.recommendations.length > 0;
  const hasAxisData = Object.values(data.axes).some(
    (axis) => !isScoredItemEmpty(axis)
  );
  const hasRiskTypeData = Object.values(data.riskTypes).some(
    (riskType) => !isScoredItemEmpty(riskType)
  );

  return (
    hasIndicator ||
    hasJustification ||
    hasEvidence ||
    hasRecommendations ||
    hasAxisData ||
    hasRiskTypeData
  );
};
